import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

export interface AddToCollectionDialogProps {
  recipeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NEW = '__new__';

/** Minimal "add to collection" flow (POST /collections/:id/recipes). */
export function AddToCollectionDialog({
  recipeId,
  open,
  onOpenChange,
}: AddToCollectionDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const collections = useQuery({
    queryKey: queryKeys.collections.all,
    queryFn: () => api.collections.list({ limit: 100 }),
    enabled: open,
    retry: false,
  });
  const [collectionId, setCollectionId] = useState<string>(NEW);
  const [newName, setNewName] = useState('');
  useEffect(() => {
    const first = collections.data?.items[0];
    if (first && collectionId === NEW && newName === '') setCollectionId(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections.data]);

  const add = useMutation({
    mutationFn: async () => {
      let id = collectionId;
      if (id === NEW) {
        const created = await api.collections.create({
          name: newName.trim() || t('recipes.collections.defaultName'),
        });
        id = created.id;
      }
      return api.collections.addRecipes(id, { recipeIds: [recipeId] });
    },
    onSuccess: (detail) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections.all });
      toast.success(t('recipes.collections.added', { name: detail.name }));
      onOpenChange(false);
    },
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="add-to-collection-dialog">
        <DialogHeader>
          <DialogTitle>{t('recipes.collections.title')}</DialogTitle>
          <DialogDescription>{t('recipes.collections.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <div>
            <Label htmlFor="collection-select" className="mb-1 block">
              {t('recipes.collections.collection')}
            </Label>
            <Select value={collectionId} onValueChange={setCollectionId}>
              <SelectTrigger id="collection-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {collections.data?.items.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} ({c.recipeCount})
                  </SelectItem>
                ))}
                <SelectItem value={NEW}>{t('recipes.collections.new')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {collectionId === NEW && (
            <div>
              <Label htmlFor="collection-new-name" className="mb-1 block">
                {t('recipes.collections.newName')}
              </Label>
              <Input
                id="collection-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={120}
                required
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={add.isPending} data-testid="add-to-collection-submit">
              {t('recipes.collections.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
